import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { ensureConfig } from '../../lib/config';
import {
  AUTH_LOST_EVENT,
  clearSession,
  completeNewPassword as completeNewPasswordApi,
  getSession,
  isAuthenticated,
  login as loginApi,
} from '../../lib/staffAuth';
import type { AuthChallenge, AuthSession } from '../../types/auth';

interface AuthContextValue {
  ready: boolean;
  isLoggedIn: boolean;
  session: AuthSession | null;
  challenge: AuthChallenge | null;
  login: (email: string, password: string) => Promise<{ challenge?: AuthChallenge }>;
  completeNewPassword: (email: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren): JSX.Element {
  const [ready, setReady] = useState(false);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [challenge, setChallenge] = useState<AuthChallenge | null>(null);

  useEffect(() => {
    void ensureConfig().then(() => {
      setSession(getSession());
      setReady(true);
    });
  }, []);

  useEffect(() => {
    const onAuthLost = () => {
      setSession(null);
      setChallenge(null);
    };
    window.addEventListener(AUTH_LOST_EVENT, onAuthLost);
    return () => window.removeEventListener(AUTH_LOST_EVENT, onAuthLost);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await loginApi(email, password);
    if (result.challenge) {
      setChallenge(result.challenge);
      return { challenge: result.challenge };
    }
    setChallenge(null);
    setSession(result.session ?? getSession());
    return {};
  }, []);

  const completeNewPassword = useCallback(
    async (email: string, newPassword: string) => {
      if (!challenge?.session) throw new Error('No pending password challenge');
      const next = await completeNewPasswordApi(email, newPassword, challenge.session);
      setChallenge(null);
      setSession(next);
    },
    [challenge],
  );

  const logout = useCallback(async () => {
    clearSession();
    setSession(null);
    setChallenge(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      ready,
      isLoggedIn: Boolean(session && isAuthenticated()),
      session,
      challenge,
      login,
      completeNewPassword,
      logout,
    }),
    [ready, session, challenge, login, completeNewPassword, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
